import type { FileEntry } from '@/types/SixDetail.types';
import type { AppListenerEffectAPI } from '@/store/listeners/types';
import { fetchSixDetailMetaAndFilesThunk } from '@/store/thunks/sixDetail/fetchMetaAndFilesThunk';
import { refreshFilesFromApi } from '@/store/thunks/sixDetail/refreshFilesThunk';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_DURATION_MS = 60000;
const MAX_ATTEMPTS = MAX_POLL_DURATION_MS / POLL_INTERVAL_MS;

const activeRefreshPolls = new Map<string, ReturnType<AppListenerEffectAPI['fork']>>();

function hasFileListChanged(initialFiles: FileEntry[], nextFiles: FileEntry[]): boolean {
  const initialCids = new Set(initialFiles.map((file) => file.cid));

  return (
    nextFiles.length !== initialFiles.length ||
    nextFiles.some((file) => !initialCids.has(file.cid))
  );
}

export function ensureFileRefreshPoll(sixId: string, listenerApi: AppListenerEffectAPI): void {
  if (activeRefreshPolls.has(sixId)) {
    return;
  }

  const initialFiles = listenerApi.getState().sixDetail.files;

  const task = listenerApi.fork(async(forkApi) => {
    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const result = await listenerApi.dispatch(refreshFilesFromApi(sixId)).unwrap();

        if (!result.skipped && hasFileListChanged(initialFiles, result.files)) {
          await listenerApi.dispatch(fetchSixDetailMetaAndFilesThunk(sixId)).unwrap();
          return true;
        }

        if (attempt < MAX_ATTEMPTS - 1) {
          await forkApi.delay(POLL_INTERVAL_MS);
        }
      }

      return false;
    } finally {
      if (activeRefreshPolls.get(sixId) === task) {
        activeRefreshPolls.delete(sixId);
      }
    }
  });

  activeRefreshPolls.set(sixId, task);
}

export function cancelAllFileRefreshPolls(): void {
  activeRefreshPolls.forEach((task) => {
    task.cancel();
  });
  activeRefreshPolls.clear();
}
